<template>
  <section class="px-11 pt-[72px] sm:pt-4 pb-12">
    <Breadcrumbs :items="data.breadcrumbs" />
    <div class="flex gap-6 flex-col lg:flex-row">
      <div>
        <h1 class="mt-4 lg:mt-10 font-medium">{{ data.title }}</h1>
        <p class="mt-6 font-medium heading04" v-html="data.description"></p>
        <div class="mt-12 inline-grid grid-cols-1 lg:grid-cols-2 gap-7">
          <div v-for="feature of data.features" :key="feature" class="flex gap-2 items-center">
            <v-icon
              name="check-circle-line"
              class="text-success-300 h-[22px] w-[22px] shrink-0"
              :size="-5"
              aria-hidden="true"
            />
            <p class="text-neutral-900 font-semibold base-bold m-0 p-0">{{ feature }}</p>
          </div>
        </div>
        <div class="mt-12 flex gap-4 flex-wrap">
          <link-wrapper
            v-for="button of visibleButtons"
            :key="button.href"
            :href="button.href"
            :aria-label="button.text"
          >
            <v-button
              :label="button.text"
              connotation="cta"
              class="link-button"
              :appearance="button.primary ? 'filled' : 'outlined'"
              :icon="button.icon"
              size="expanded"
            />
          </link-wrapper>
        </div>
      </div>
      <img
        v-if="data.imageUrl"
        class="mt-3 h-[443px] w-auto order-first lg:order-last"
        :src="getFullPathForBrandedAssets(data.imageUrl)"
        :alt="data.title"
      />
    </div>
    <div class="mt-7 flex gap-4 -mx-6 flex-wrap">
      <link-wrapper v-for="link of computedLinks" :key="link.href" :href="link.href" class="text-canvas-text">
        <div class="flex gap-4 items-center py-4 px-6 rounded-md hover:bg-neutral-50">
          <img :src="getFullPathForPublicAssets(link.iconUrl)" :alt="'Get More Information about ' + link.text" />
          <div class="font-semibold base-bold">{{ link.text }}</div>
        </div>
      </link-wrapper>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ProductHeaderProps } from '@bifrost-dev-docs-mfe/common/types/compositeComponents/ProductHeader';
import { VIcon, VButton } from '@vonage/vivid-vue';
import { computed, PropType } from 'vue';
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import { getFullPathForPublicAssets, getFullPathForBrandedAssets } from '@/utils/url';
import useUserStore from '@/store/user';
import LinkWrapper from '@/components/markdown/widgets/LinkWrapper.vue';

const props = defineProps({
  data: {
    type: Object as PropType<ProductHeaderProps>,
    required: true,
  },
});

const user = useUserStore();
const isSignedIn = computed(() => user.info?.status === 'logged-in');

const visibleButtons = computed(() => {
  const signedInStatus = isSignedIn.value ? 'logged-in' : 'logged-out';
  return props.data.buttons.filter(button => !button.showWhen || button.showWhen === signedInStatus);
});

const computedLinks = computed(() => (props.data.links ? props.data.links : []));
</script>

<style lang="scss" scoped>
// Remove default styles
a {
  margin: 0 !important;
  text-decoration: none !important;
}
.base-bold {
  font: var(--vvd-typography-base-bold);
}
.heading04 {
  font: var(--vvd-typography-heading-4);
  font-weight: var(--dev-docs-product-description-font-weight);
  font-stretch: var(--dev-docs-product-description-font-stretch);
}
.link-button {
  --vvd-button-cta-firm: var(--dev-docs-product-landing-page-button);
}
</style>
